import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription, EMPTY, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '@admin-core/services/auth.service';
import { ChatApiService } from '@admin-core/services/chat-api.service';
import { ChatRealtimeService } from '@admin-core/services/chat-realtime.service';
import {
  ChatMessage,
  ConversationDetail,
  ConversationListItem
} from '@admin-core/models/chat.model';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';

/** Temporary REST fallback only while SignalR is fully closed. */
const FALLBACK_POLL_MS = 8_000;

@Component({
  selector: 'app-chat-admin',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe, TranslatePipe],
  templateUrl: './chat-admin.component.html',
  styleUrl: './chat-admin.component.scss'
})
export class ChatAdminComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesPane') private messagesPane?: ElementRef<HTMLElement>;
  private stickToLatest = false;

  private readonly api = inject(ChatApiService);
  private readonly auth = inject(AuthService);
  private readonly realtime = inject(ChatRealtimeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly conversations = signal<ConversationListItem[]>([]);
  readonly selected = signal<ConversationDetail | null>(null);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly draft = signal('');
  readonly error = signal<string | null>(null);
  readonly mute = this.realtime.mute;

  private selectedId: string | null = null;
  private subs = new Subscription();
  private boundBusinessKey: string | null = null;
  private pendingClientMessageId: string | null = null;
  private pendingClientMessageBody: string | null = null;
  private readonly onVisibilityChange = () => this.markOpenConversationRead();

  constructor() {
    effect(() => {
      const detail = this.selected();
      const count = detail?.messages.length ?? 0;
      if (count > 0) {
        this.stickToLatest = true;
      }
    });

    effect(() => {
      const key = this.auth.businessKey();
      if (!key || key === this.boundBusinessKey) {
        return;
      }
      const hadBusiness = this.boundBusinessKey != null;
      this.boundBusinessKey = key;
      if (!hadBusiness) {
        return;
      }
      this.selectedId = null;
      this.selected.set(null);
      this.conversations.set([]);
      this.error.set(null);
      if (this.router.url.startsWith('/admin/chat/') && this.router.url !== '/admin/chat') {
        void this.router.navigateByUrl('/admin/chat');
      }
      this.loadList();
    });
  }

  ngAfterViewChecked(): void {
    if (!this.stickToLatest) {
      return;
    }
    this.stickToLatest = false;
    this.scrollToBottom();
  }

  ngOnInit(): void {
    this.loadList();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.subs.add(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('conversationId');
        this.selectedId = id;
        if (id) {
          this.openConversation(id);
        } else {
          this.selected.set(null);
        }
      })
    );

    this.subs.add(
      this.realtime.conversationUpserted$.subscribe((upsert) => {
        this.conversations.update((list) => {
          const next = list.filter((c) => c.id !== upsert.id);
          return [this.clearBadgeWhenOpen(upsert), ...next];
        });
      })
    );

    this.subs.add(
      this.realtime.messageReceived$.subscribe((msg) => {
        if (!this.selectedId || msg.conversationId !== this.selectedId) {
          return;
        }
        this.appendMessage(msg);
        if (msg.senderType === 'Visitor') {
          this.markOpenConversationRead();
        }
      })
    );

    // After SignalR reconnect: one REST sync, then realtime only.
    this.subs.add(
      this.realtime.adminSyncNeeded$.subscribe(() => {
        this.syncInboxOnce();
        this.syncSelectedOnce();
      })
    );

    // Fallback polling only when hub is closed (not during automatic reconnect).
    this.subs.add(
      this.realtime.adminFallback$
        .pipe(switchMap((active) => (active ? timer(FALLBACK_POLL_MS, FALLBACK_POLL_MS) : EMPTY)))
        .subscribe(() => {
          this.syncInboxOnce();
          this.syncSelectedOnce();
        })
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.subs.unsubscribe();
  }

  loadList(): void {
    this.loading.set(true);
    this.api.adminList().subscribe({
      next: (items) => {
        this.conversations.set(items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.error ?? 'Could not load conversations.');
        this.loading.set(false);
      }
    });
  }

  openConversation(id: string): void {
    this.api.adminGet(id).subscribe({
      next: (detail) => {
        this.selected.set(detail);
        this.markRead(id);
      },
      error: () => {
        this.selected.set(null);
        void this.router.navigateByUrl('/admin/chat');
      }
    });
  }

  send(): void {
    const id = this.selectedId;
    const body = this.draft().trim();
    if (!id || !body || this.sending()) {
      return;
    }

    if (this.pendingClientMessageBody !== body) {
      this.pendingClientMessageId = crypto.randomUUID();
      this.pendingClientMessageBody = body;
    }
    const clientMessageId = this.pendingClientMessageId!;

    this.sending.set(true);
    this.api.adminReply(id, body, clientMessageId).subscribe({
      next: (result) => {
        this.pendingClientMessageId = null;
        this.pendingClientMessageBody = null;
        this.appendMessage(result.message);
        this.draft.set('');
        this.sending.set(false);
        this.conversations.update((list) => {
          const next = list.filter((c) => c.id !== result.conversation.id);
          return [result.conversation, ...next];
        });
      },
      error: (err) => {
        const message = err?.error?.error ?? 'Could not send reply.';
        this.error.set(message);
        this.sending.set(false);
      }
    });
  }

  toggleMute(): void {
    this.realtime.setMute(!this.mute());
    if (!this.mute()) {
      this.realtime.notifyVisitorMessage();
    }
  }

  private markOpenConversationRead(): void {
    const id = this.selectedId;
    if (!id || document.visibilityState !== 'visible') {
      return;
    }
    this.markRead(id);
  }

  private markRead(id: string): void {
    this.api.adminMarkRead(id).subscribe({
      next: () => {
        this.conversations.update((list) =>
          list.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c))
        );
        this.api.unreadCount(true).subscribe({
          next: (u) => this.realtime.setUnread(u.count)
        });
      }
    });
  }

  private clearBadgeWhenOpen(item: ConversationListItem): ConversationListItem {
    return item.id === this.selectedId && document.visibilityState === 'visible'
      ? { ...item, unreadCount: 0 }
      : item;
  }

  private syncInboxOnce(): void {
    this.api.adminList(true).subscribe({
      next: (items) => {
        this.conversations.set(items.map((c) => this.clearBadgeWhenOpen(c)));
      },
      error: () => undefined
    });
  }

  private lastSelectedMessageId(): string | null {
    const messages = this.selected()?.messages;
    if (!messages?.length) {
      return null;
    }
    return messages[messages.length - 1]?.id ?? null;
  }

  private syncSelectedOnce(): void {
    const id = this.selectedId;
    if (!id) {
      return;
    }
    const afterMessageId = this.lastSelectedMessageId();
    this.api.adminGet(id, { silent: true, afterMessageId }).subscribe({
      next: (detail) => {
        if (!detail || detail.id !== this.selectedId) {
          return;
        }
        this.selected.update((current) => {
          if (!current) {
            return detail;
          }

          if (!afterMessageId) {
            return detail;
          }

          if (detail.messages.length === 0) {
            return current;
          }

          const byId = new Map(current.messages.map((m) => [m.id, m]));
          for (const m of detail.messages) {
            byId.set(m.id, m);
          }
          const merged = [...byId.values()].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          return { ...current, ...detail, messages: merged };
        });

        const msgs = this.selected()?.messages ?? [];
        if (msgs.some((m) => m.senderType === 'Visitor' && !m.isReadByAdmin)) {
          this.markOpenConversationRead();
        }
      },
      error: () => undefined
    });
  }

  private appendMessage(message: ChatMessage): void {
    this.selected.update((detail) => {
      if (!detail || detail.messages.some((m) => m.id === message.id)) {
        return detail;
      }
      return { ...detail, messages: [...detail.messages, message] };
    });
  }

  private scrollToBottom(): void {
    const el = this.messagesPane?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }
}
